import React, { useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Divider,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Alert,
  IconButton,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
} from "@mui/material";
import {
  Assessment as AssessmentIcon,
  SportsSoccer as SportsSoccerIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  HelpOutline as HelpOutlineIcon,
} from "@mui/icons-material";

const StatsForm = ({ athlete, onStatsSave, onCancel, initialStats = {} }) => {
  const theme = useTheme();
  const [formData, setFormData] = useState({
    // General stats
    pffScore: initialStats.pffScore || "",
    ppiScore: initialStats.ppiScore || "",
    paiScore: initialStats.paiScore || "",
    gamesPlayed: initialStats.gamesPlayed || "",
    gamesStarted: initialStats.gamesStarted || "",
    totalSnaps: initialStats.totalSnaps || "",

    // Passing stats
    passing: {
      completions: initialStats.passing?.completions || "",
      attempts: initialStats.passing?.attempts || "",
      yards: initialStats.passing?.yards || "",
      touchdowns: initialStats.passing?.touchdowns || "",
      interceptions: initialStats.passing?.interceptions || "",
      yardsPerAttempt: initialStats.passing?.yardsPerAttempt || "",
      yardsPerGame: initialStats.passing?.yardsPerGame || "",
    },

    // Rushing stats
    rushing: {
      attempts: initialStats.rushing?.attempts || "",
      yards: initialStats.rushing?.yards || "",
      touchdowns: initialStats.rushing?.touchdowns || "",
      yardsPerCarry: initialStats.rushing?.yardsPerCarry || "",
      yardsPerGame: initialStats.rushing?.yardsPerGame || "",
    },

    // Receiving stats
    receiving: {
      receptions: initialStats.receiving?.receptions || "",
      yards: initialStats.receiving?.yards || "",
      touchdowns: initialStats.receiving?.touchdowns || "",
      yardsPerReception: initialStats.receiving?.yardsPerReception || "",
      yardsPerGame: initialStats.receiving?.yardsPerGame || "",
    },
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle input changes
  const handleInputChange = (section, field, value) => {
    if (section) {
      setFormData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[field] || (section && errors[`${section}.${field}`])) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (section) {
          delete newErrors[`${section}.${field}`];
        } else {
          delete newErrors[field];
        }
        return newErrors;
      });
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    // Validate numeric fields
    const numericFields = [
      "pffScore",
      "ppiScore",
      "paiScore",
      "gamesPlayed",
      "gamesStarted",
      "totalSnaps",
    ];

    numericFields.forEach((field) => {
      if (formData[field] && (isNaN(formData[field]) || formData[field] < 0)) {
        newErrors[field] = "Must be a positive number";
      }
    });

    // Validate passing stats
    if (formData.passing.attempts && formData.passing.completions) {
      if (
        parseFloat(formData.passing.completions) >
        parseFloat(formData.passing.attempts)
      ) {
        newErrors["passing.completions"] = "Completions cannot exceed attempts";
      }
    }

    // Validate rushing stats
    if (formData.rushing.attempts && formData.rushing.yards) {
      if (parseFloat(formData.rushing.yards) < 0) {
        newErrors["rushing.yards"] = "Yards cannot be negative";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Clean up the data - remove empty strings and convert to numbers where appropriate
      const cleanedData = {};

      // Clean general stats
      Object.keys(formData).forEach((key) => {
        if (key === "passing" || key === "rushing" || key === "receiving") {
          // Handle nested objects
          const cleanedSection = {};
          Object.keys(formData[key]).forEach((subKey) => {
            if (formData[key][subKey] !== "") {
              cleanedSection[subKey] = isNaN(formData[key][subKey])
                ? formData[key][subKey]
                : parseFloat(formData[key][subKey]);
            }
          });
          if (Object.keys(cleanedSection).length > 0) {
            cleanedData[key] = cleanedSection;
          }
        } else if (formData[key] !== "") {
          cleanedData[key] = isNaN(formData[key])
            ? formData[key]
            : parseFloat(formData[key]);
        }
      });

      await onStatsSave(cleanedData);
    } catch (error) {
      console.error("Error saving stats:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get position-specific fields to show
  const getPositionFields = () => {
    const position = athlete.position?.toUpperCase();

    if (position === "QB" || position === "QUARTERBACK") {
      return ["passing"];
    } else if (position === "RB" || position === "RUNNING BACK") {
      return ["rushing", "receiving"];
    } else if (
      position === "WR" ||
      position === "WIDE RECEIVER" ||
      position === "TE" ||
      position === "TIGHT END"
    ) {
      return ["receiving"];
    } else {
      // For other positions, show all sections
      return ["passing", "rushing", "receiving"];
    }
  };

  const positionFields = getPositionFields();

  return (
    <Paper elevation={0} variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, color: "primary.main" }} />
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          {Object.keys(initialStats).length > 0 ? "Edit" : "Initialize"}{" "}
          Performance Statistics
        </Typography>
        <Tooltip title="All fields are optional. Fill in the data you have available.">
          <IconButton size="small" sx={{ ml: 1 }}>
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          {Object.keys(initialStats).length > 0
            ? "Update the prospect's performance statistics. All fields are optional - you can modify what you have and add more."
            : "This prospect currently has no performance statistics. Use this form to initialize their stats. All fields are optional - you can fill in what you have and add more later."}
        </Typography>
      </Alert>

      <form onSubmit={handleSubmit}>
        {/* General Performance Metrics */}
        <Accordion defaultExpanded sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <SportsSoccerIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                General Performance Metrics
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="PFF Score"
                  type="number"
                  value={formData.pffScore}
                  onChange={(e) =>
                    handleInputChange(null, "pffScore", e.target.value)
                  }
                  error={!!errors.pffScore}
                  helperText={
                    errors.pffScore || "Pro Football Focus score (0-100)"
                  }
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="PPI Score"
                  type="number"
                  value={formData.ppiScore}
                  onChange={(e) =>
                    handleInputChange(null, "ppiScore", e.target.value)
                  }
                  error={!!errors.ppiScore}
                  helperText={
                    errors.ppiScore || "Player Performance Index score"
                  }
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="PAI Score"
                  type="number"
                  value={formData.paiScore}
                  onChange={(e) =>
                    handleInputChange(null, "paiScore", e.target.value)
                  }
                  error={!!errors.paiScore}
                  helperText={errors.paiScore || "Player Athletic Index score"}
                  inputProps={{ min: 0, step: 0.1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Games Played"
                  type="number"
                  value={formData.gamesPlayed}
                  onChange={(e) =>
                    handleInputChange(null, "gamesPlayed", e.target.value)
                  }
                  error={!!errors.gamesPlayed}
                  helperText={
                    errors.gamesPlayed || "Total games played this season"
                  }
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Games Started"
                  type="number"
                  value={formData.gamesStarted}
                  onChange={(e) =>
                    handleInputChange(null, "gamesStarted", e.target.value)
                  }
                  error={!!errors.gamesStarted}
                  helperText={
                    errors.gamesStarted || "Total games started this season"
                  }
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  fullWidth
                  label="Total Snaps"
                  type="number"
                  value={formData.totalSnaps}
                  onChange={(e) =>
                    handleInputChange(null, "totalSnaps", e.target.value)
                  }
                  error={!!errors.totalSnaps}
                  helperText={
                    errors.totalSnaps || "Total snaps played this season"
                  }
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
            </Grid>
          </AccordionDetails>
        </Accordion>

        {/* Passing Statistics */}
        {positionFields.includes("passing") && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                Passing Statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Completions"
                    type="number"
                    value={formData.passing.completions}
                    onChange={(e) =>
                      handleInputChange(
                        "passing",
                        "completions",
                        e.target.value
                      )
                    }
                    error={!!errors["passing.completions"]}
                    helperText={
                      errors["passing.completions"] || "Passing completions"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Attempts"
                    type="number"
                    value={formData.passing.attempts}
                    onChange={(e) =>
                      handleInputChange("passing", "attempts", e.target.value)
                    }
                    error={!!errors["passing.attempts"]}
                    helperText={
                      errors["passing.attempts"] || "Passing attempts"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Passing Yards"
                    type="number"
                    value={formData.passing.yards}
                    onChange={(e) =>
                      handleInputChange("passing", "yards", e.target.value)
                    }
                    error={!!errors["passing.yards"]}
                    helperText={
                      errors["passing.yards"] || "Total passing yards"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Touchdowns"
                    type="number"
                    value={formData.passing.touchdowns}
                    onChange={(e) =>
                      handleInputChange("passing", "touchdowns", e.target.value)
                    }
                    error={!!errors["passing.touchdowns"]}
                    helperText={
                      errors["passing.touchdowns"] || "Passing touchdowns"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Interceptions"
                    type="number"
                    value={formData.passing.interceptions}
                    onChange={(e) =>
                      handleInputChange(
                        "passing",
                        "interceptions",
                        e.target.value
                      )
                    }
                    error={!!errors["passing.interceptions"]}
                    helperText={
                      errors["passing.interceptions"] || "Passing interceptions"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Yards Per Attempt"
                    type="number"
                    value={formData.passing.yardsPerAttempt}
                    onChange={(e) =>
                      handleInputChange(
                        "passing",
                        "yardsPerAttempt",
                        e.target.value
                      )
                    }
                    error={!!errors["passing.yardsPerAttempt"]}
                    helperText={
                      errors["passing.yardsPerAttempt"] ||
                      "Average yards per attempt"
                    }
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Yards Per Game"
                    type="number"
                    value={formData.passing.yardsPerGame}
                    onChange={(e) =>
                      handleInputChange(
                        "passing",
                        "yardsPerGame",
                        e.target.value
                      )
                    }
                    error={!!errors["passing.yardsPerGame"]}
                    helperText={
                      errors["passing.yardsPerGame"] ||
                      "Average passing yards per game"
                    }
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Rushing Statistics */}
        {positionFields.includes("rushing") && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                Rushing Statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Rush Attempts"
                    type="number"
                    value={formData.rushing.attempts}
                    onChange={(e) =>
                      handleInputChange("rushing", "attempts", e.target.value)
                    }
                    error={!!errors["rushing.attempts"]}
                    helperText={
                      errors["rushing.attempts"] || "Rushing attempts"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Rushing Yards"
                    type="number"
                    value={formData.rushing.yards}
                    onChange={(e) =>
                      handleInputChange("rushing", "yards", e.target.value)
                    }
                    error={!!errors["rushing.yards"]}
                    helperText={
                      errors["rushing.yards"] || "Total rushing yards"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Rush Touchdowns"
                    type="number"
                    value={formData.rushing.touchdowns}
                    onChange={(e) =>
                      handleInputChange("rushing", "touchdowns", e.target.value)
                    }
                    error={!!errors["rushing.touchdowns"]}
                    helperText={
                      errors["rushing.touchdowns"] || "Rushing touchdowns"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Yards Per Carry"
                    type="number"
                    value={formData.rushing.yardsPerCarry}
                    onChange={(e) =>
                      handleInputChange(
                        "rushing",
                        "yardsPerCarry",
                        e.target.value
                      )
                    }
                    error={!!errors["rushing.yardsPerCarry"]}
                    helperText={
                      errors["rushing.yardsPerCarry"] ||
                      "Average yards per carry"
                    }
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Yards Per Game"
                    type="number"
                    value={formData.rushing.yardsPerGame}
                    onChange={(e) =>
                      handleInputChange(
                        "rushing",
                        "yardsPerGame",
                        e.target.value
                      )
                    }
                    error={!!errors["rushing.yardsPerGame"]}
                    helperText={
                      errors["rushing.yardsPerGame"] ||
                      "Average rushing yards per game"
                    }
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Receiving Statistics */}
        {positionFields.includes("receiving") && (
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
                Receiving Statistics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Receptions"
                    type="number"
                    value={formData.receiving.receptions}
                    onChange={(e) =>
                      handleInputChange(
                        "receiving",
                        "receptions",
                        e.target.value
                      )
                    }
                    error={!!errors["receiving.receptions"]}
                    helperText={
                      errors["receiving.receptions"] || "Total receptions"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Receiving Yards"
                    type="number"
                    value={formData.receiving.yards}
                    onChange={(e) =>
                      handleInputChange("receiving", "yards", e.target.value)
                    }
                    error={!!errors["receiving.yards"]}
                    helperText={
                      errors["receiving.yards"] || "Total receiving yards"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Receiving Touchdowns"
                    type="number"
                    value={formData.receiving.touchdowns}
                    onChange={(e) =>
                      handleInputChange(
                        "receiving",
                        "touchdowns",
                        e.target.value
                      )
                    }
                    error={!!errors["receiving.touchdowns"]}
                    helperText={
                      errors["receiving.touchdowns"] || "Receiving touchdowns"
                    }
                    inputProps={{ min: 0, step: 1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Yards Per Reception"
                    type="number"
                    value={formData.receiving.yardsPerReception}
                    onChange={(e) =>
                      handleInputChange(
                        "receiving",
                        "yardsPerReception",
                        e.target.value
                      )
                    }
                    error={!!errors["receiving.yardsPerReception"]}
                    helperText={
                      errors["receiving.yardsPerReception"] ||
                      "Average yards per reception"
                    }
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    fullWidth
                    label="Receiving Yards Per Game"
                    type="number"
                    value={formData.receiving.yardsPerGame}
                    onChange={(e) =>
                      handleInputChange(
                        "receiving",
                        "yardsPerGame",
                        e.target.value
                      )
                    }
                    error={!!errors["receiving.yardsPerGame"]}
                    helperText={
                      errors["receiving.yardsPerGame"] ||
                      "Average receiving yards per game"
                    }
                    inputProps={{ min: 0, step: 0.1 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Action Buttons */}
        <Box
          sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={onCancel}
            disabled={isSubmitting}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isSubmitting}
            startIcon={<SaveIcon />}
            sx={{ minWidth: 120 }}
          >
            {isSubmitting
              ? "Saving..."
              : Object.keys(initialStats).length > 0
              ? "Update Stats"
              : "Save Stats"}
          </Button>
        </Box>
      </form>
    </Paper>
  );
};

export default StatsForm;
